/*
Description: This is the file that will handle interacting with the Cora Health EMR
Date: 1/16/2024
Author: FNU Kajol 

Notes:
    All Injectors need the following functions:
        getPageInfo
        getPatientInfo
        getQuestions
        answerQuestions (Consider combining these and making the single a private function)
        answerQuestion
*/

export function getPageInfo() {
  // function getPageInfo() {
  /*
      Description: This function will get the page info from the note
      Return: A dictionary with the page info
          {
              "URL": Base URL for the page,
              "Note Type": "PT Daily/SOAP Note",...
          }
      */
  let baseURL = window.location.origin;
  // remove "-uat" from the baseurl
  // Check if baseURL exist and it has "-uat"
  if (baseURL && baseURL.includes('-uat')) {
    baseURL = baseURL.replace('-uat', '');
  }

  const noteTypeElement = document.querySelector(
    '[class*="PatientSpecificTab_tab_selected"] [class*="PatientSpecificTab_tab_contents_name"]'
  );

  let noteType = noteTypeElement ? noteTypeElement.textContent : null;

  // If there is a dash in the note type then we need to split it and take the first part and strip
  if (noteType && noteType.includes('-')) {
    noteType = noteType.split('-')[0].trim();
  }

  return {
    URL: baseURL,
    'Note Type': noteType
  };
}

export function getPageInfoForCapture() {
  return {
    ...getPageInfo(),
    Metadata: readNoteMetadataForCapture(),
    'Date of Visit': readTreatmentDateForCapture()
  };
}

export function getPatientInfo() {
  /*
      Description: This function will get the patient info from the note
      Return: A dictionary with the patient info
      {
          "EMR ID": 0x1234 or "michalskij"
          "First Name":firstName,
          "Last Name": lastName,
          "DOB": dob,
      }
      */

  const patientElement = document.querySelector(
    '[class*="Note_patient_header"] > span'
  );

  const patientName = patientElement ? patientElement.textContent : '';

  let firstName = '';
  let lastName = '';
  let emrID = '';

  if (patientName) {
    const nameParts = patientName.split(' ');
    firstName = nameParts[0];
    lastName = nameParts[1];
    emrID = `${lastName},${firstName}`.replace(/\s/g, '');
  }
  const dob = '05/29/2023';

  return {
    'EMR ID': emrID,
    'First Name': firstName,
    'Last Name': lastName,
    DOB: dob
  };
}

export function getPatientInfoForCapture() {
  return getPatientInfo();
}

/* 
  questions - This is where we will store the questions
            - a list of dictionaries
       [{
          "Question": "Where was the injury?", 
          "Type": "Multiple Choice", 
          "Choices": ["Back", "Neck", "Shoulder"]
       },
          ...
          ]
  
  */
var questions = [];
var questionInteractions = {};

export async function getQuestions() {
  questionInteractions = {};
  questions = [];
  return buildQuestions(questions, questionInteractions, false);
}

/**
 * Return current note values keyed by question key. Flex values preserve their
 * nested section and field keys.
 */
export async function getQuestionsForCapture(): Promise<Record<string, any>> {
  const captureQuestions = [];
  const captureQuestionInteractions = {};
  const currentQuestions = await buildQuestions(
    captureQuestions,
    captureQuestionInteractions,
    true
  );
  return extractCaptureValues(currentQuestions);
}

async function buildQuestions(
  questions,
  questionInteractions,
  captureScrape
) {
  try {
    // Todo handle subjective detials and assessment seperately for daily note and evaluation both
    // Step 1: Fetch all the questions
    await fetchFlexQuestionsForSubjectiveAssessmentPlan(questionInteractions, captureScrape);
    await fetchFlexQuestionsForObjectiveActivityGoals(
      questionInteractions,
      captureScrape
    );
    await getCheckBoxQuestions(questionInteractions, captureScrape);

    // Step 2: Filter those we don't want
    if (Object.keys(questionInteractions).length > 0) {
      Object.entries(questionInteractions).forEach(([key, q]) => {
        const structureIsEmpty =
          q.Type === 'Flex' &&
          (!q.Structure ||
            Object.values(q.Structure).every(
              (val) => typeof val === 'object' && Object.keys(val).length === 0
            ));

        const shouldDelete =
          (q.Key && q.Key.includes('Objective-Templates')) ||
          (q.Choices && q.Choices.length < 2) ||
          (q.Key &&
            (q.Key === 'Objective' ||
              q.Key === 'Goals - Short Term Goals Goals')) ||
          structureIsEmpty ||
          (q.Key && q.Key.includes('Functional Deficits'));

        if (shouldDelete) {
          delete questionInteractions[key];
        }
      });
    }

    // Step 3: Sort the questions based on Dom order
    // This list must be ordered based on where the OriginalElement (DOM element) is located in the document order
    Object.keys(questionInteractions)
      .sort((a, b) => {
        const elementA = questionInteractions[a].OriginalElement;
        const elementB = questionInteractions[b].OriginalElement;
        if (
          elementA.compareDocumentPosition(elementB) &
          Node.DOCUMENT_POSITION_FOLLOWING
        ) {
          return -1;
        } else {
          return 1;
        }
      })
      .forEach((key) => {
        const question = cleanSubsectionNames({ ...questionInteractions[key] });
        delete question.OriginalElement;
        questions.push(question);
      });

    console.log('Sorted questions:', questions);
    questions.forEach((question) => {
      console.log(question.Key);
    });

    return questions; // Return the final array of questions
  } catch (error) {
    console.error('Error in getQuestions:', error);
    return []; // Return an empty array if an error occurs
  }
}

function cleanSubsectionNames(question) {
  if (question?.Type !== 'Flex' || !question.Structure) return question;
  const section = question.Key.replace(/^(Subjective|Objective|Assessment|Plan) /, '');
  const cleanKeys = (fields) => {
    if (!fields || typeof fields !== 'object' || Array.isArray(fields)) return fields;
    const original = Object.keys(fields);
    const cleaned = original.map((name) => {
      const prefix = [`${section} - `, `${section}-`].find((prefix) => name.startsWith(prefix));
      return prefix ? name.slice(prefix.length) : name;
    });
    const unsafe = [...original, ...cleaned].some((name) => !name || name.replace(/["']/g, '').trim() !== name);
    const collision = cleaned.some((name, index) =>
      cleaned.indexOf(name) !== index || (name !== original[index] && original.includes(name)));
    if (unsafe || collision) return fields;
    return Object.fromEntries(original.map((name, index) => [cleaned[index], fields[name]]));
  };
  // View More uses row keys; nested field keys keep their complete suffixes.
  const rows = cleanKeys(question.Structure);
  return { ...question, Structure: Object.fromEntries(Object.entries(rows).map(([name, fields]) =>
    [name, cleanKeys(fields)])) };
}

function extractCaptureValues(questions) {
  const values = {};

  questions.forEach((question, index) => {
    const key = question?.Key || question?.Question || String(index);
    const value = getCaptureValue(question);
    if (value !== null && value !== undefined) {
      values[key] = value;
    }
  });

  return values;
}

function getCaptureValue(question) {
  if (question.ExistingNote !== null && question.ExistingNote !== undefined) {
    return question.ExistingNote;
  }
  if (
    question['Existing Value'] !== null &&
    question['Existing Value'] !== undefined
  ) {
    return question['Existing Value'];
  }
  if (question.Structure) {
    const values = extractFlexCaptureValues(question.Structure);
    return Object.keys(values).length > 0 ? values : null;
  }
  return null;
}

function extractFlexCaptureValues(structure) {
  const values = {};

  Object.entries(structure || {}).forEach(([key, value]) => {
    if (!value || typeof value !== 'object') return;

    if (
      value['Existing Value'] !== null &&
      value['Existing Value'] !== undefined
    ) {
      values[key] = value['Existing Value'];
      return;
    }

    const nested = extractFlexCaptureValues(value);
    if (Object.keys(nested).length > 0) {
      values[key] = nested;
    }
  });

  return values;
}

export function getNoteKeyForCapture() {
  const noteId = readNoteMetadataForCapture()?.NoteId;
  if (typeof noteId !== 'string' && typeof noteId !== 'number') return null;

  const normalizedNoteId = String(noteId).trim();
  return normalizedNoteId ? `note:${normalizedNoteId}` : null;
}

export const noteFinalizationSelectorForCapture = '#charges [title="Save Final"]';

function readNoteMetadataForCapture(): Record<string, any> | null {
  const metadataElement = document.querySelector('#noteMetadata');
  if (!metadataElement?.textContent) return null;

  try {
    const metadata = JSON.parse(metadataElement.textContent);
    return metadata && typeof metadata === 'object' && !Array.isArray(metadata)
      ? metadata
      : null;
  } catch (_) {
    return null;
  }
}

function readTreatmentDateForCapture(): string | null {
  const input = document.querySelector(
    '#charges input[placeholder="m/d/yyyy"]'
  ) as HTMLInputElement | null;
  return input?.value.trim() || null;
}

async function fetchFlexQuestionsForObjectiveActivityGoals(
  questionInteractions,
  captureScrape
) {
  let finalPrcoessingSections = [];
  // Query section labels dynamically
  const sectionLabels = document.querySelectorAll("[class*='Section_label']");

  sectionLabels.forEach((labelElement) => {
    const sectionLabel = labelElement.textContent.trim();
    finalPrcoessingSections.push(sectionLabel);
    // Dynamically find the parent section
    const parentSection =
      labelElement.closest("[class*='Section_header_wrapper']") ||
      labelElement.closest("[class*='Section_wrapper']");
    const childWrapper = parentSection
      ? parentSection.nextElementSibling
      : null;

    // Ensure valid child wrapper
    if (
      childWrapper &&
      (childWrapper.classList.contains(
        '[class*="Section_child_wrapper_no_collapse"]'
      ) ||
        childWrapper.classList.contains('[class*="Section_child_wrapper"]') ||
        childWrapper.matches("[class*='Section_child_wrapper']") ||
        observationCheckArray.some((section) => sectionLabel.includes(section)))
    ) {
      let check = sectionLabel; // default fallback
      let subsectionLabel = sectionLabel;

      const sectionWrapper = labelElement.closest("[class*='Section_wrapper']");
      const outerWrapper = sectionWrapper?.closest(
        '[class*="Section_child_wrapper"]'
      );
      const parentHeader = outerWrapper?.previousElementSibling;
      const parentLabelEl = parentHeader?.querySelector(
        "[class*='Section_label']"
      );
      const parentLabel = parentLabelEl?.textContent?.trim();

      if (parentLabel === 'Observations') {
        check = 'Objective Observations';
        subsectionLabel = sectionLabel;
      } else {
        // fallback logic for non-Observations groupings
        const sectionMapping = {
          Charges: 'Charges',
          Plan: 'Plan',
          Assessment: 'Assessment',
          Goals: 'Goals - Short Term Goals',
          'Activity Log': 'Activity Log',
          Objective: 'Objective',
          Subjective: 'Subjective'
        };

        for (const key in sectionMapping) {
          if (finalPrcoessingSections.includes(key)) {
            // Handle the case where the section label is part of the key
            if (sectionMapping[key] !== sectionLabel) {
              check = `${sectionMapping[key]} ${sectionLabel}`;
            } else {
              check = sectionMapping[key];
            }
            break;
          }
        }
      }
      if (
        (check.startsWith('Activity Log') && check !== 'Activity Log') ||
        check == 'Objective' ||
        check == 'Objective Objective' ||
        check == 'Objective Templates' ||
        check == 'Objective My Templates' ||
        check.startsWith('Subjective') ||
        check.startsWith('Assessment') ||
        check.startsWith('Charges') ||
        check.startsWith('Plan')
      ) {
        console.warn(`Skipping processing for section: ${sectionLabel}`);
        return; // Skip processing for other Activity Log sections
      }
      // Select all rows dynamically
      const rows = Array.from(
        childWrapper.querySelectorAll("[class*='_row__']")
      );
      if (!questionInteractions[check]) {
        questionInteractions[check] = {
          Type: 'Flex',
          Key: check,
          Question: check,
          Structure: {},
          OriginalElement: childWrapper,
          ReplyType:
            check.startsWith('Activity Log') || check.startsWith('Goals')
              ? 'no_reply'
              : 'overwrite'
        };
      }

      let accumulatedLabels = []; // Stores labels from rows that have data
      let prefixLabels = new Set(); // Final combined labels
      let stopAtRow = null; // Stop before the first input/select row

      rows.forEach((row, rowIndex) => {
        // Check if row contains input/select elements
        const hasInput = row.querySelector(
          "textarea, select, input[type='text']"
        );

        if (hasInput && stopAtRow === null) {
          stopAtRow = rowIndex; // Stop processing labels before this row
        }

        const labels = Array.from(
          row.querySelectorAll("div[class*='header'], span[class*='header']")
        )
          .map((header) => header.textContent.trim())
          .filter((text) => text.length > 0);

        // If labels exist, store them
        if (labels.length > 0) {
          accumulatedLabels.push(labels);
        }

        // Stop accumulating once we reach an input/select row
        if (rowIndex === stopAtRow) {
          if (accumulatedLabels.length >= 2) {
            // If we have at least two non-empty rows, make combinations
            for (let i = 0; i < accumulatedLabels.length - 1; i++) {
              for (let j = i + 1; j < accumulatedLabels.length; j++) {
                accumulatedLabels[i].forEach((label1) => {
                  accumulatedLabels[j].forEach((label2) => {
                    prefixLabels.add(`${label1}-${label2}`);
                  });
                });
              }
            }
          } else {
            // If only one row had data, just use that
            prefixLabels = new Set(accumulatedLabels.flat());
          }
        }

        // Convert the Set to an array for further processing
        const prefixArray = Array.from(prefixLabels);

        // Process other rows, appending prefix labels dynamically
        const labelElement =
          row.querySelector('label') || row.querySelector("[class*='header']");
        let baseLabel = labelElement
          ? labelElement.textContent.trim()
          : `Row ${rowIndex + 1}`;
        if (sectionLabel !== check) {
          baseLabel = `${sectionLabel} - ${baseLabel}`;
        }
        if (baseLabel.includes('Observations')) {
          return;
        }
        // if (baseLabel.endsWith(' - Comments') || baseLabel === 'Comments') {
        //   return; // Skip Comments rows entirely (omit from structure)
        // }
        const inputs = Array.from(
          row.querySelectorAll("textarea, select, input[type='text']")
        );

        if (inputs.length > 0) {
          // Initialize baseLabel structure if it doesn't exist
          if (!questionInteractions[check].Structure[baseLabel]) {
            questionInteractions[check].Structure[baseLabel] = {};
          }

          let combinedLabels = prefixArray.map(
            (prefix) => `${sectionLabel}-${prefix}-${baseLabel}`
          );
          combinedLabels = cleanMotionLabels(combinedLabels);

          // When there's only one input (single lowest-level field) and the row is Comments, use "Comments" as the key (don't repeat table header)
          // Without this (And before we were skipping it, it was doing Comments - AARom Toe 5 - Comments...)
          if (
            inputs.length === 1 &&
            (baseLabel.endsWith(' - Comments') || baseLabel === 'Comments')
          ) {
            combinedLabels = ['Comments'];
          }

          // Map inputs to sub-labels dynamically
          inputs.forEach((input, index) => {
            if (input.disabled) return; // Skip disabled inputs
            const inputType = input.tagName.toLowerCase();

            let type = determineInputType(labelElement); // Default to string

            let subLabel = combinedLabels[index] || `SubLabel-${index + 1}`;
            if (inputType === 'select') {
              let options = Array.from(input.options).map((option) =>
                option.textContent.trim()
              );
              options = options.filter(
                (option) => option !== 'N/A' && option !== ''
              );
              options.push('None');
              const existingValue =
                input.options[input.selectedIndex]?.textContent?.trim() || '';
              questionInteractions[check].Structure[baseLabel][subLabel] = {
                ...(type === 'Integer' && { Type: type }),
                ...(captureScrape && { 'Existing Value': existingValue }),
                Choices: options,
                Input: (value) => {
                  let existingValue =
                    questionInteractions[check].Structure[baseLabel][subLabel]
                      ?.Value;
                  if (isEmptyOrNullorSameAsExisting(value, existingValue)) {
                    return;
                  }
                  const matchingOption = Array.from(input.options).find(
                    (option) => option.textContent.trim() === value
                  );

                  if (matchingOption) {
                    input.value = matchingOption.value;
                    input.dispatchEvent(new Event('input', { bubbles: true }));
                    input.dispatchEvent(new Event('change', { bubbles: true }));
                  } else {
                    console.warn(
                      `Value "${value}" not found in select options for "${baseLabel} - ${subLabel}".`
                    );
                  }
                }
              };
            } else if (inputType === 'textarea' || inputType === 'input') {
              const actMaxLen =
                'maxLength' in (input as HTMLInputElement) && (input as HTMLInputElement).maxLength > 0
                  ? (input as HTMLInputElement).maxLength
                  : null;
              // ROM accepts WNL/WFL, so send as string
              const isROM = type === 'Integer' && /range of motion/i.test(subLabel);
              const description = [
                isROM &&
                  'Enter degrees as a whole number. Use WNL (within normal limits) or WFL (within functional limits) only if explicitly stated. Do not infer or estimate. Leave blank if not stated.',
                actMaxLen && `Your response must not exceed ${actMaxLen} characters.`
              ].filter(Boolean).join(' ');
              questionInteractions[check].Structure[baseLabel][subLabel] = {
                ...(type === 'Integer' && !isROM && { Type: type }),
                ...(captureScrape && { 'Existing Value': input.value }),
                ...(description && { Description: description }),
                Input: (value, replyType) => {
                  let existingValue =
                    questionInteractions[check].Structure[baseLabel][subLabel]
                      ?.Value;
                  if (isEmptyOrNullorSameAsExisting(value, existingValue)) {
                    return;
                  }
                  if (replyType === 'append') {
                    input.value = `${input.value || ''}\n${value}`.trim();
                  } else if (replyType === 'overwrite') {
                    input.value = value;
                  }
                  input.dispatchEvent(new Event('input', { bubbles: true }));
                  input.dispatchEvent(new Event('change', { bubbles: true }));
                }
              };
            } else {
              console.warn(
                `Unsupported input type "${inputType}" for "${baseLabel} - ${subLabel}".`
              );
            }
          });
        } else {
          console.warn(
            `Row ${rowIndex + 1} in section "${sectionLabel}" has no valid label or input elements.`
          );
        }
      });
    } else {
      console.warn(`Section "${sectionLabel}" has no child wrapper.`);
    }
  });
  handleHandROMSection(questionInteractions, captureScrape);
  handleActivityLogSection(questionInteractions);
  handleGoalsSection(questionInteractions);
  console.log('Flex Interactions:', questionInteractions);
}

async function fetchFlexQuestionsForSubjectiveAssessmentPlan(
  questionInteractions,
  captureScrape
) {
  const finalProcessingSections = [];
  const sectionLabels = document.querySelectorAll("[class*='Section_label']");

  sectionLabels.forEach((labelElement) => {
    const sectionLabel = labelElement.textContent.trim();
    finalProcessingSections.push(sectionLabel);

    const parentSection =
      labelElement.closest("[class*='Section_header_wrapper']") ||
      labelElement.closest("[class*='Section_wrapper']");
    const childWrapper = parentSection
      ? parentSection.nextElementSibling
      : null;

    if (
      !childWrapper ||
      !childWrapper.matches("[class*='Section_child_wrapper']")
    )
      return;

    let check = '';
    const sectionMapping = {
      Charges: 'Charges',
      Plan: 'Plan',
      Assessment: 'Assessment',
      Goals: 'Goals',
      'Activity Log': 'Activity Log',
      Objective: 'Objective',
      Subjective: 'Subjective'
    };

    for (const key in sectionMapping) {
      if (finalProcessingSections.includes(key)) {
        check =
          sectionMapping[key] !== sectionLabel
            ? `${sectionMapping[key]} ${sectionLabel}`
            : sectionMapping[key];
        const noteType = getNoteType();
        if (shouldSkipSection(check, noteType)) return;
        break;
      }
    }

    initInteractionStructure(questionInteractions, check, sectionLabel, labelElement);

    const inputContainers = childWrapper.querySelectorAll(
      "[class*='_row__'], [class*='radio_row'], [class*='input_row'], [class*='Common_row_not_full'], .react-datepicker-wrapper, [class*='Common_container'], [class*='Common_body_wrapper']"
    );

    inputContainers.forEach((container) => {
      const inputs = Array.from(
        container.querySelectorAll(
          "textarea, select, input[type='text'], input[type='radio'], input[type='checkbox'], .react-datepicker-wrapper input[type='text']"
        )
      );
      inputs.forEach((input) => {
        if (input.disabled) return;

        const inputType = input.tagName.toLowerCase();
        let type = determineInputType(input);
        const baseLabel = getBaseLabel(input, container);

        if (inputType === 'select')
          handleSelectInput(
            questionInteractions,
            input,
            sectionLabel,
            baseLabel,
            check,
            type,
            captureScrape
          );
        else if (inputType === 'input' && input.type === 'radio')
          handleRadioInput(
            questionInteractions,
            input,
            sectionLabel,
            baseLabel,
            check,
            container
          );
        else if (
          inputType === 'textarea' ||
          (inputType === 'input' && input.type === 'text')
        )
          handleTextInput(
            questionInteractions,
            input,
            sectionLabel,
            baseLabel,
            check,
            type
          );
      });
    });
  });
  // Note: Functional Deficits section is automatically handled by Goals autoselect when Goals are filled
  handleFunctionalDeficitsSection(questionInteractions, captureScrape);
  getLongAnswerQuestions(questionInteractions);
  console.log('📊 Final Flex Interactions:', questionInteractions);
}

const observationCheckArray = [
  'Posture',
  'Gait',
  'Observation and General Appearance',
  'Circumference Measurements',
  'Palpation',
  'Accessory Joint Mobility',
  'Functional Testing'
];
function cleanMotionLabels(labels) {
  const dedupeTargets = [
    'Cervical Flexion °',
    'Cervical Extension °',
    'Thoracic Flexion °',
    'Thoracic Extension °',
    'Lumbar Flexion °',
    'Lumbar Extension °'
  ];

  const seen = new Set();

  return labels.reduce((acc, label) => {
    const shouldNormalize = dedupeTargets.some((target) =>
      label.includes(target)
    );
    const cleaned = shouldNormalize ? label.replace(/-[LR]-/, '-') : label;

    if (!seen.has(cleaned)) {
      seen.add(cleaned);
      acc.push(cleaned);
    }

    return acc;
  }, []);
}

function createInputHandler(input) {
  return (value) => {
    let existingValue = input?.value;
    if (isEmptyOrNullorSameAsExisting(value, existingValue)) return;
    input.value = value;
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  };
}

function handleHandROMSection(questionInteractions, captureScrape) {
  // --------- 1. Handle strength-based hand and wrist (#str_hand) ----------
  const selectSection = document.querySelector('#str_hand');
  if (selectSection) {
    const rows = Array.from(
      selectSection.querySelectorAll('div[class*="Common_row"]')
    );
    let currentFinger = null;

    rows.forEach((row) => {
      const label = row.querySelector('[class*="Common_rowTitle"]');
      if (label && label.textContent.trim()) {
        currentFinger = label.textContent.trim();
      }

      const selects = row.querySelectorAll('select');
      if (selects.length === 0 || !currentFinger) return;

      const sectionKey = `Objective - Strenght - Hand and Wrist`;
      if (!questionInteractions[sectionKey]) {
        questionInteractions[sectionKey] = {
          Type: 'Flex',
          Key: sectionKey,
          Question: sectionKey,
          Structure: {},
          OriginalElement: selectSection,
          ReplyType: 'overwrite'
        };
      }

      if (!questionInteractions[sectionKey].Structure[currentFinger]) {
        questionInteractions[sectionKey].Structure[currentFinger] = {};
      }

      selects.forEach((select) => {
        const name = select.getAttribute('name') || '';
        const parsed = name.match(
          /^ST([A-Z]+?)(MC[P]?|PI[P]?|DI[P]?|IP)?(EXTENSION|FLEXION|ABDUCTION|ADDUCTION|OPPOSITION)?(R|L)$/i
        );
        if (!parsed) return;

        const [, fingerPart, joint, movement, side] = parsed;
        const labelKey = `${joint} ${side} ${movement}`;

        const options = Array.from(select.options)
          .map((opt) => opt.textContent.trim())
          .filter((opt) => opt && opt !== 'N/A' && opt !== '');
        options.push('None');

        questionInteractions[sectionKey].Structure[currentFinger][labelKey] = {
          Description: `Select all applicable answers. If none apply, reply with ‘None’. Provide your response as a list of choices separated by a ‘$’ symbol. The available options are: ${options.join(', ')}`,
          Choices: options,
          ...(captureScrape && {
            'Existing Value':
              select.options[select.selectedIndex]?.textContent?.trim() || ''
          }),
          Input: (value) => {
            let existingValue =
              questionInteractions[sectionKey].Structure[currentFinger][
                labelKey
              ]?.Value;
            if (isEmptyOrNullorSameAsExisting(value, existingValue)) return;
            const option = Array.from(select.options).find(
              (opt) => opt.textContent.trim() === value
            );
            if (option) {
              select.value = option.value;
              select.dispatchEvent(new Event('input', { bubbles: true }));
              select.dispatchEvent(new Event('change', { bubbles: true }));
            }
          }
        };
      });
    });
  }

  // --------- 2. Handle Objective - Hand and Wrist ROM inputs (#obj_hand) ----------
  const textSection = document.querySelector('#obj_hand');
  if (!textSection) return;

  const rows = Array.from(
    textSection.querySelectorAll('div[class*="Common_row"]')
  );
  let currentLabel = null;
  let currentSubsection = 'AROM';

  rows.forEach((row) => {
    const sectionHeader = row.querySelector('[class*="Common_center"]');
    if (sectionHeader) {
      const sectionText = sectionHeader.textContent.trim().toUpperCase();
      if (sectionText.includes('PROM')) currentSubsection = 'PROM';
      else if (sectionText.includes('AROM')) currentSubsection = 'AROM';
    }

    const labelEl = row.querySelector('[class*="Common_rowTitle"]');
    if (labelEl && labelEl.textContent.trim()) {
      currentLabel = labelEl.textContent.trim();
    }

    const inputs = Array.from(row.querySelectorAll("input[type='text']"));
    if (inputs.length === 0 || !currentLabel) return;

    const sectionKey = `Objective - Hand - ${currentSubsection}`;
    if (!questionInteractions[sectionKey]) {
      questionInteractions[sectionKey] = {
        Type: 'Flex',
        Key: sectionKey,
        Question: sectionKey,
        Structure: {},
        OriginalElement: textSection,
        ReplyType: 'overwrite'
      };
    }

    if (!questionInteractions[sectionKey].Structure[currentLabel]) {
      questionInteractions[sectionKey].Structure[currentLabel] = {};
    }

    const isThumb =
      currentLabel.toLowerCase().includes('thumb') ||
      currentLabel.toLowerCase().includes('digit');

    if (!isThumb && inputs.length === 12) {
      const mapping = [
        ['MC', 'R', 'Ext', 0],
        ['MC', 'R', 'Flex', 1],
        ['MC', 'L', 'Ext', 2],
        ['MC', 'L', 'Flex', 3],
        ['PIP', 'R', 'Ext', 4],
        ['PIP', 'R', 'Flex', 5],
        ['PIP', 'L', 'Ext', 6],
        ['PIP', 'L', 'Flex', 7],
        ['DIP', 'R', 'Ext', 8],
        ['DIP', 'R', 'Flex', 9],
        ['DIP', 'L', 'Ext', 10],
        ['DIP', 'L', 'Flex', 11]
      ];

      mapping.forEach(([joint, side, movement, index]) => {
        const key = `${currentLabel} ${joint} ${side} ${movement}`;
        const input = inputs[index];
        questionInteractions[sectionKey].Structure[currentLabel][key] = {
          ...(captureScrape && { 'Existing Value': input.value }),
          Input: createInputHandler(input)
        };
      });
    } else {
      inputs.forEach((input, index) => {
        const side = index === 0 ? 'R' : 'L';
        const fieldKey = `${currentLabel} ${side}`;
        questionInteractions[sectionKey].Structure[currentLabel][fieldKey] = {
          ...(captureScrape && { 'Existing Value': input.value }),
          Input: createInputHandler(input)
        };
      });
    }
  });
}

// Utility to extract note type
function getNoteType() {
  const tab = document.querySelector(
    '[class*="PatientSpecificTab_tab_selected"] [class*="PatientSpecificTab_tab_contents_name"]'
  );
  if (!tab) return '';
  let noteType = tab.textContent;
  return noteType.includes('-')
    ? noteType.split('-')[0].trim()
    : noteType.trim();
}

function shouldSkipSection(check, noteType) {
  return (
    (check === 'Subjective' && noteType === 'Eval') ||
    check.startsWith('Objective') ||
    check.startsWith('Activity Log') ||
    check.startsWith('Goals') ||
    check.startsWith('Charges')
  );
}

function initInteractionStructure(
  questionInteractions,
  check,
  sectionLabel,
  labelElement
) {
  if (!questionInteractions[check]) {
    questionInteractions[check] = {
      Type: 'Flex',
      Key: check,
      Question: check,
      Structure: {},
      OriginalElement: labelElement
    };
  }
  if (!questionInteractions[check].Structure[sectionLabel]) {
    questionInteractions[check].Structure[sectionLabel] = {};
  }
}
const selectInputCounter = {};
const selectInputMap = new WeakMap();

function handleSelectInput(
  questionInteractions,
  input,
  sectionLabel,
  baseLabel,
  check,
  type,
  captureScrape
) {
  const options = Array.from(input.options)
    .map((option) => option.textContent.trim())
    .filter((opt) => opt !== 'N/A' && opt !== 'None');
  options.push('None');
  const existingValue =
    input.options[input.selectedIndex]?.textContent?.trim() || '';

  if (check === 'Plan') {
    const label = getDynamicPlanLabel(input);

    if (!selectInputMap.has(input)) {
      if (!selectInputCounter[label]) {
        selectInputCounter[label] = 1;
      }
      selectInputMap.set(
        input,
        `${label} - ${selectInputCounter[label]++}`
      );
    }

    const numberedLabel = selectInputMap.get(input);

    questionInteractions[check].Structure[sectionLabel][numberedLabel] = {
      ...(type === 'Integer' && { Type: type }),
      ...(captureScrape && { 'Existing Value': existingValue }),
      Choices: options,
      Input: (value) => {
        let existingValue =
          questionInteractions[check].Structure[sectionLabel][numberedLabel]
            ?.Value;
        if (isEmptyOrNullorSameAsExisting(value, existingValue)) return;
        const match = Array.from(input.options).find(
          (option) => option.textContent.trim() === value
        );
        if (match) {
          input.value = match.value;
          simulateSelectChange(input);
        } else {
          console.warn(
            `Value "${value}" not found in select options for "${numberedLabel}".`
          );
        }
      }
    };
  } else {
    // Match each select with its immediately preceding label
    const labelEl = input.closest('div')?.previousElementSibling;
    let labelText = '';
    if (labelEl && labelEl.tagName === 'LABEL') {
      labelText = labelEl.textContent.trim().replace(/:$/, '');
    } else {
      // Use a more generic selector for the container, resilient to class name suffix changes
      const container = input.closest('div[class^="Pain_rating_row__"]');
      const allLabels = Array.from(container?.querySelectorAll('label') || []);
      const allSelects = Array.from(
        container?.querySelectorAll('select') || []
      );
      const index = allSelects.indexOf(input);
      labelText =
        allLabels[index]?.textContent.trim().replace(/:$/, '') ||
        `${baseLabel} ${index + 1}`;
    }

    questionInteractions[check].Structure[sectionLabel][labelText] = {
      ...(type === 'Integer' && { Type: type }),
      ...(captureScrape && { 'Existing Value': existingValue }),
      Choices: options,
      Input: (value) => {
        let existingValue =
          questionInteractions[check].Structure[sectionLabel][labelText]?.Value;
        if (isEmptyOrNullorSameAsExisting(value, existingValue)) return;
        const match = Array.from(input.options).find(
          (opt) => opt.textContent.trim() === value
        );
        if (match) {
          input.value = match.value;
          simulateSelectChange(input);
        } else {
          console.warn(
            `Value "${value}" not found in select options for "${labelText}".`
          );
        }
      }
    };
  }
}

function simulateSelectChange(input) {
  function fireEvents() {
    // Dispatch focus
    input.dispatchEvent(
      new FocusEvent('focusin', {
        bubbles: true,
        relatedTarget: document.activeElement
      })
    );

    // Dispatch click
    input.dispatchEvent(
      new PointerEvent('click', {
        bubbles: true,
        pointerId: 1,
        width: 1,
        height: 1,
        pressure: 0.5
      })
    );

    // Dispatch input
    input.dispatchEvent(new Event('input', { bubbles: true }));

    // Dispatch change
    input.dispatchEvent(new Event('change', { bubbles: true }));

    // Optional: Dispatch blur if needed
    input.dispatchEvent(
      new FocusEvent('focusout', {
        bubbles: true,
        relatedTarget: null
      })
    );

    ['border-color', 'box-shadow'].forEach((prop) =>
      input.dispatchEvent(
        new TransitionEvent('transitionend', {
          bubbles: true,
          propertyName: prop,
          elapsedTime: 0.15
        })
      )
    );
  }

  fireEvents();

  setTimeout(() => {
    fireEvents();
  }, 250); // second run to force reactivity (esp. in CORA PDC fields)
}

function handleRadioInput(
  questionInteractions,
  input,
  sectionLabel,
  baseLabel,
  check,
  container
) {
  const radioLabel = input.nextElementSibling?.textContent.trim();
  const radioGroupLabel = baseLabel || sectionLabel;
  const structure = questionInteractions[check].Structure[sectionLabel];

  if (!structure[radioGroupLabel]) {
    structure[radioGroupLabel] = {
      Choices: [],
      Input: (value) => {
        let existingValue =
          questionInteractions[check].Structure[sectionLabel][radioGroupLabel]
            ?.Value;
        if (isEmptyOrNullorSameAsExisting(value, existingValue)) return;

        // Step 1: Find a reasonable high-up wrapper (entire section or group)
        const sectionRoot =
          container.closest('[class*="MedHistory_radio_row"]') ||
          container.closest('[class*="Section_child_wrapper"]') ||
          container;
        // Step 2: Find ALL radios under that section
        const radios = sectionRoot.querySelectorAll('input[type="radio"]');
        radios.forEach((radio) => {
          const labelEl = radio.nextElementSibling;
          const labelText = labelEl?.textContent?.trim();
          const isAlreadySelected = radio.checked;

          if (!isAlreadySelected && labelText === value) {
            radio.click(); // only click if not already selected
            radio.dispatchEvent(new Event('input', { bubbles: true }));
            radio.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      }
    };
  }
  if (!structure[radioGroupLabel]?.Choices?.includes(radioLabel)) {
    structure[radioGroupLabel]?.Choices?.push(radioLabel);
  }
  if (input.checked) {
    structure[radioGroupLabel]['Existing Value'] = radioLabel;
  }
}

function handleTextInput(
  questionInteractions,
  input,
  sectionLabel,
  baseLabel,
  check,
  type
) {
  if (check === 'Assessment') return;
  if (baseLabel?.includes('seen')) type = 'Integer';
  let description = '';
  if (
    input?.placeholder?.includes('m/d/yyyy') ||
    input?.classList?.contains('[class*="DBDateField_picker"]')
  ) {
    description = 'Enter the date in the format MM/DD/YYYY';
  }
  const maxLen = 'maxLength' in input && input.maxLength > 0 ? input.maxLength : null;
  if (maxLen && maxLen != -1) {
    description = description
      ? `${description}. Your response must not exceed ${maxLen} characters.`
      : `Your response must not exceed ${maxLen} characters.`;
  }
  questionInteractions[check].Structure[sectionLabel][baseLabel] = {
    ...(type === 'Integer' && { Type: type }),
    ...(description && { Description: description }),
    'Existing Value': input.value,
    Input: (value, replyType) => {
      let existingValue =
        questionInteractions[check].Structure[sectionLabel][baseLabel]?.Value;
      if (isEmptyOrNullorSameAsExisting(value, existingValue)) return;
      if (replyType === 'append') {
        input.value = `${input.value || ''}\n${value}`.trim();
      } else if (replyType === 'overwrite') {
        input.value = value;
      }
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };
}

function getLongAnswerQuestions(questionInteractions) {
  const textAreas = [
    {
      selector: '[class*="Contraindications_textarea"]',
      key: 'Contraindications'
    },
    { selector: '[class*="History_details_field"]', key: 'Subjective Details' },
    {
      selector: '#assessment textarea[class*="Common_textarea"]',
      key: 'Assessment Details'
    },
    {
      selector: '#assessment textarea[class*="Common_simple_textarea"]',
      key: 'Assessment Details'
    },
    {
      selector: '#assessment textarea[class*="Common_req_textarea"]',
      key: 'Assessment Details'
    },
    {
      selector: 'textarea[class^="NonEval_textarea__"]',
      key: 'Subjective Details'
    }
  ];

  textAreas.forEach(({ selector, key }) => {
    const el = document.querySelector(selector);
    if (el) {
      questionInteractions[key] = {
        Type: 'Long Answer',
        Question: key,
        Key: key,
        ExistingNote: el.value.trim(),
        OriginalElement: el,
        Input: async (value, replyType) => {
          let existingValue = questionInteractions[key].ExistingNote;
          if (isEmptyOrNullorSameAsExisting(value, existingValue)) return;
          el.value =
            replyType === 'append'
              ? `${el.value || ''}\n${value}`.trim()
              : value;
          el.dispatchEvent(new Event('input', { bubbles: true }));
          el.dispatchEvent(new Event('change', { bubbles: true }));
        }
      };
    }
  });
}

async function getCheckBoxQuestions(questionInteractions, captureScrape) {
  // Find all checkbox inputs in the document
  const checkboxes = document.querySelectorAll('input[type="checkbox"]');

  checkboxes.forEach((checkbox) => {
    let row = checkbox.closest('[class*="Common_row__"]'); // Find its row
    let label = findClosestLabel(row)
      ? 'Objective Observations ' + findClosestLabel(row)
      : extractHierarchy(checkbox); // Get closest label or hierarchy
    if (!label) return; // Skip if no label is found")
    let sectionKey = null;

    let base = label;

    // look for label in exisitng flex questionInteractions
    while (!sectionKey) {
      if (questionInteractions[base]) {
        sectionKey = base;
        break;
      }

      if (base?.includes(' ')) {
        const parts = base.split(' ');
        parts.pop(); // remove last word
        base = parts.join(' ').trim();
      } else {
        // Reached the most reduced form
        break;
      }
    }

    // Step 3: Fallback to whatever base is left if no match
    if (!sectionKey) {
      sectionKey = base;
    }

    const subLabel = `${label} - Check Box`;
    if (subLabel.includes('Objective Strength - Check Box')) return;
    let choiceLabel = checkbox.nextElementSibling?.textContent.trim(); // Get checkbox text
    if (subLabel.includes('Plan') && choiceLabel === 'Eval Only') {
      // Skip if the checkbox is for Eval Only
      return;
    }
    if (!choiceLabel) return; // Skip if no checkbox text is found
    if (!questionInteractions[sectionKey]) {
      questionInteractions[sectionKey] = {
        Key: sectionKey,
        Question: sectionKey,
        Type: 'Flex',
        Structure: {},
        OriginalElement:
          checkbox.closest('[class*="Section_child_wrapper"]') || checkbox,
        ReplyType:
          sectionKey === 'Goals' || sectionKey === 'Activity Log'
            ? 'no_reply'
            : 'overwrite'
      };
    }
    if (!questionInteractions[sectionKey].Structure[subLabel]) {
      questionInteractions[sectionKey].Structure[subLabel] = {};
    }

    if (!questionInteractions[sectionKey].Structure[subLabel][subLabel]) {
      questionInteractions[sectionKey].Structure[subLabel][subLabel] = {};
    }

    const checkboxInteraction =
      questionInteractions[sectionKey].Structure[subLabel][subLabel];
    checkboxInteraction.Input = (selectedOptions) => {
      let existingValue =
        questionInteractions[sectionKey].Structure[subLabel][subLabel]?.Value;
      if (isEmptyOrNullorSameAsExisting(selectedOptions, existingValue)) return;
      checkboxes.forEach((box) => {
        const boxLabel = box
          .closest('[class*="CheckBox_checkbox_wrapper_enabled"]')
          ?.querySelector('[class*="CheckBox_checkbox_label"]')
          ?.textContent.trim();
        if (boxLabel?.trim().toLowerCase() === 'none') return;

        if (boxLabel && selectedOptions.includes(boxLabel) && !box.checked) {
          box.click();
        }
      });
    };

    if (captureScrape) {
      const existingValue = checkboxInteraction['Existing Value'];
      if (checkbox.checked) {
        checkboxInteraction['Existing Value'] = existingValue
          ? `${existingValue}$${choiceLabel}`
          : choiceLabel;
      } else if (existingValue === undefined) {
        checkboxInteraction['Existing Value'] = '';
      }
    }

    const desc =
      checkboxInteraction.Description ||
      `Select all applicable answers. If none apply, reply with ‘None’. Provide your response as a list of choices separated by a ‘$’ symbol. Return ONLY values. Never return instruction text from any Description fields. Valid options to choose from:`;
    checkboxInteraction.Description = desc + choiceLabel + ',';
  });
  console.log('Final CheckBox Interactions:', questionInteractions);
}

const labelCounters = {}; // Add this at the top of your file once

function getBaseLabel(input, container) {
  const labelCounters = {};
  const getCleanText = (el) => el?.textContent?.trim().replace(/:$/, '') || '';

  // 1. Equipment Rows (checkboxes)
  const equipRow = input.closest('[class*="Equip_row"]');
  if (equipRow) {
    const rowLabel = equipRow.querySelector('[class*="Equip_col1"]');
    const checkboxLabel = input
      .closest('.container')
      ?.querySelector('[class*="CheckBox_checkbox_label"]')
      ?.textContent?.trim();

    if (rowLabel) {
      const base = getCleanText(rowLabel);
      return checkboxLabel ? `${base} - ${checkboxLabel}` : base;
    }
    if (checkboxLabel) return checkboxLabel;
  }

  // 2. NonEval Rows (dropdowns and subjective inputs)
  const nonEvalRow = input.closest('[class*="NonEval_row"]');
  if (nonEvalRow) {
    const labelEl = nonEvalRow.querySelector('label');
    const labelText = getCleanText(labelEl);
    if (labelText) return labelText;

    const tag = input.tagName.toLowerCase();
    let baseLabel;
    if (tag === 'select') baseLabel = 'Subjective Dropdown';
    else if (tag === 'textarea') baseLabel = 'Subjective Free Text';
    else if (input.type === 'text') baseLabel = 'Subjective Text Input';

    if (baseLabel) {
      labelCounters[baseLabel] = (labelCounters[baseLabel] || 0) + 1;
      return `${baseLabel} ${labelCounters[baseLabel]}`;
    }
  }

  // 3. Medications / Supplements rows
  const medsRow = input.closest('[class*="Meds_row"]');
  if (medsRow) {
    const labelEl = medsRow.querySelector('[class*="Meds_col1_top"]');
    return getCleanText(labelEl) || 'Supplements';
  }

  // 4. Equipment Textareas (Other)
  const equipTextAreaRow = input.closest('[class*="Equip_row"]');
  if (equipTextAreaRow) {
    const labelEl = equipTextAreaRow.querySelector('[class*="Equip_col1_top"]');
    return getCleanText(labelEl) || 'Other Equipment';
  }

  // 5. Date Fields
  const isDateField = input.closest('.react-datepicker-wrapper');
  if (isDateField) {
    return getDateLabel(input);
  }

  // 6. Fallback for Text Inputs (e.g., Height, BP)
  const rowInputs = Array.from(
    container.querySelectorAll('input[type="text"]')
  );
  const labels = Array.from(container.querySelectorAll('label'));
  const inputIndex = rowInputs.indexOf(input);

  const baseLabel = getCleanText(labels[0]) || 'Field';

  if (rowInputs.length === 1) return baseLabel;

  const unitLabel = labels
    .find(
      (label) =>
        label.compareDocumentPosition(input) & Node.DOCUMENT_POSITION_PRECEDING
    )
    ?.textContent.trim();

  if (!unitLabel || unitLabel === baseLabel) {
    return `${baseLabel} ${inputIndex + 1}`;
  }

  return `${baseLabel} ${unitLabel}`;
}

function getDateLabel(input) {
  const isDateField = input.closest('.react-datepicker-wrapper');
  if (!isDateField) return null;

  const dateWrapper = isDateField.closest('[class*="DBDateField_wrapper"]');
  if (!dateWrapper) return null;

  const spanWrapper = dateWrapper.closest('span');
  let label = null;

  if (spanWrapper) {
    // Try previousElementSibling of the span
    let maybeLabel = spanWrapper.previousElementSibling;
    while (maybeLabel && maybeLabel.tagName !== 'LABEL') {
      maybeLabel = maybeLabel.previousElementSibling;
    }
    if (maybeLabel && maybeLabel.textContent.trim()) {
      label = maybeLabel.textContent.trim().replace(/:$/, '');
    }
  }

  // Fallback — scan all labels in grandparent wrapper
  if (!label) {
    const sectionWrapper = dateWrapper.closest(
      '[class*="History_date_wrapper"]'
    );
    if (sectionWrapper) {
      const allLabels = Array.from(sectionWrapper.querySelectorAll('label'));
      const following = allLabels.find(
        (lbl) =>
          lbl.compareDocumentPosition(dateWrapper) &
          Node.DOCUMENT_POSITION_FOLLOWING
      );
      if (following && following.textContent.trim()) {
        label = following.textContent.trim().replace(/:$/, '');
      }
    }
  }

  return label;
}

function getDynamicPlanLabel(input) {
  const container = input.closest('div[class*="Common_entry_wrapper"]');
  const wrapper = container?.closest('div[class*="Common_sub_cat_wrapper"]');
  const labelEl = wrapper?.previousElementSibling;
  const labelText = labelEl?.textContent?.trim();

  if (!labelText) return 'time(s) per';
  return labelText;
}

function isPedsNote() {
  // Any selector that means the note is PEDS
  const pedsSelectors = [
    'span.Common_goal_header_title__xcy9o', // <span>Peds Goal</span>
    '[class*="obj_obs_posture_peds"]', // Objective posture Peds
    '[class*="MedHistoryPeds_table__"]' // Medical History table Peds
  ];

  // Loop through the selectors
  for (const sel of pedsSelectors) {
    if (document.querySelector(sel)) {
      return true;
    }
  }
  return false;
}

function handleActivityLogSection(questionInteractions) {
  let isPeds = isPedsNote();

  const check = 'Activity Log';
  if (!questionInteractions[check]) {
    questionInteractions[check] = {
      Type: 'Flex',
      Key: check,
      Question: check,
      Structure: {},
      ReplyType: 'overwrite',
      OriginalElement: document.body
    };
  }
  // Ensure Structure exists and clear any prior entries
  questionInteractions[check].Structure = {};

  const activitySections = Array.from(
    document.querySelectorAll('div[id^="al_"]')
  ).map((section) => {
    const id = `#${section.id}`;
    const labelEl = section.querySelector('[class*="Section_label_wide"]');
    const label = labelEl?.textContent.trim() || 'Unknown Section';
    return { id, label };
  });

  activitySections.forEach(({ id, label }) => {
    const section = document.querySelector(id);
    if (!section) return;

    // Dynamically detect fields from the header row
    const headerRow = section.querySelector('div[class*="Common_row"]');
    const fields = Array.from(headerRow?.children || [])
      .map((el) => el.textContent.trim())
      .filter((text) => text && !text.includes('Remove'));

    // Handle "Total Time" field if it exists (keep flat)
    const timeLabel =
      section.querySelector('[class*="Section_time_label_req"]') ||
      section.querySelector('[class*="Section_time_label"]');
    const timeInput =
      section.querySelector('[class*="Section_time_field_req"]') ||
      section.querySelector('[class*="Section_time_field"]');
    if (timeLabel && timeInput) {
      const totalTimeKey = `${label} - Total Time`;
      if (!questionInteractions[check].Structure[totalTimeKey]) {
        questionInteractions[check].Structure[totalTimeKey] = {};
      }
      questionInteractions[check].Structure[totalTimeKey][totalTimeKey] = {
        Type: 'Integer',
        ...(timeInput.value && { 'Existing Value': timeInput.value }),
        Input: (val) => {
          if (
            timeInput.value ||
            isEmptyOrNullorSameAsExisting(val, timeInput.value)
          )
            return;
          timeInput.value = val;
          timeInput.dispatchEvent(new Event('input', { bubbles: true }));
          timeInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      };
    }
    const rows = section.querySelectorAll('div[class*="Common_row"]');
    let logCount = 1;

    Array.from(rows)
      .slice(1)
      .forEach((row) => {
        const selects = row.querySelectorAll('select');
        const textInputs = row.querySelectorAll('input[type="text"]');
        const checkbox = row.querySelector('input[type="checkbox"]');

        // --- Begin new logic for prefix and struct ---
        let toImproveLabel = '',
          activityLabel = '';
        let selectIdx = 0,
          textIdx = 0;

        fields.forEach((field) => {
          if (['To Improve'].includes(field) && selects[selectIdx]) {
            const select = selects[selectIdx];
            const selected = Array.from(select.options)[
              select.selectedIndex
            ]?.textContent?.trim();
            toImproveLabel = selected ? 'To Improve ' + selected : '';
          } else if (field === 'Activity' && textInputs[textIdx]) {
            const input = textInputs[textIdx];
            activityLabel = input.value?.trim()
              ? 'Activity ' + input.value?.trim()
              : '';
          }
        });

        const prefixBase = `${label} ${logCount++}`;
        const suffix =
          toImproveLabel || activityLabel
            ? ` (${toImproveLabel || ''}${toImproveLabel && activityLabel ? ' - ' : ''}${activityLabel || ''})`
            : '';
        const prefix = `${prefixBase}${suffix}`;

        if (!questionInteractions[check].Structure[prefix]) {
          questionInteractions[check].Structure[prefix] = {};
        }
        const struct = questionInteractions[check].Structure[prefix];
        selectIdx = 0;
        textIdx = 0;

        fields.forEach((field) => {
          if (
            [
              'To Improve',
              'Assistive Device',
              'Level of Assist',
              'Surface'
            ].includes(field) &&
            selects[selectIdx]
          ) {
            handleSelectField(field, selects[selectIdx++], struct);
          } else if (
            field === 'Activity' &&
            selects[selectIdx] &&
            selects[selectIdx].tagName.toLowerCase() === 'select'
          ) {
            handleSelectField(field, selects[selectIdx++], struct, isPeds);
          } else if (
            field === 'Activity' &&
            textInputs[textIdx] &&
            textInputs[textIdx].tagName.toLowerCase() === 'input'
          ) {
            handleInputField(field, textInputs[textIdx++], struct, isPeds);
          } else if (textInputs[textIdx]) {
            handleInputField(field, textInputs[textIdx++], struct);
          }
        });
        if (checkbox) {
          struct['Performed'] = {
            Choices: ['Yes', 'No'],
            Input: (val) => {
              if (val === 'Yes' && !checkbox.checked) {
                checkbox.click();
                checkbox.dispatchEvent(new Event('change', { bubbles: true }));
              }
            }
          };
        }
      });
  });
}
function handleSelectField(field, select, struct, isPeds) {
  const options = Array.from(select.options);
  const choices = options
    .map((opt) => opt.textContent.trim())
    .filter((opt) => opt && opt !== '-1' && opt !== '');
  const selectedLabel = options[select.selectedIndex]?.textContent?.trim();

  if (shouldSkipPreselectedField(field, selectedLabel)) {
    return;
  }
  struct[field] = {
    Choices: choices,
    ...(selectedLabel && { 'Existing Value': selectedLabel }),
    Input: (val) => {
      const isSameOrEmpty = isEmptyOrNullorSameAsExisting(val, select.value);
      if (
        (isPeds && isSameOrEmpty) ||
        (!isPeds && (select.value !== '-1' || isSameOrEmpty))
      ) {
        return;
      }
      const match = options.find((opt) => opt.textContent.trim() === val);
      if (match) {
        select.value = match.value;
        select.dispatchEvent(new Event('input', { bubbles: true }));
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  };
}

function handleInputField(field, input, struct, isPeds) {
  const inputValue = input.value;
  const type = field === 'Time' ? 'Integer' : 'String';
  if (
    (field === 'Activity' || field === 'To Improve') &&
    input.value !== '-1' &&
    input.value !== ''
  ) {
    return;
  }
  const maxLen = 'maxLength' in input && input.maxLength > 0 ? input.maxLength : null;
  struct[field] = {
    ...(type === 'Integer' && { Type: type }),
    ...(inputValue && { 'Existing Value': inputValue }),
    ...(maxLen && {
      Description: `Your response must not exceed ${maxLen} characters.`
    }),
    Input: (val) => {
      const isSameOrEmpty = isEmptyOrNullorSameAsExisting(val, input.value);
      if (
        (isPeds && isSameOrEmpty) ||
        (!isPeds && ((input.value && !field.includes('Time')) || isSameOrEmpty))
      ) {
        return;
      }
      input.value = val;
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };
}

function showFunctionalDeficitsPopup(button) {
  // remove any existing popup first
  const existing = document.querySelector('.comprehend-popup');
  if (existing) existing.remove();

  // create popup
  const popup = document.createElement('div');
  popup.classList.add('comprehend-popup');
  popup.style.cssText = `
    position: absolute;
    top: ${button.offsetTop + button.offsetHeight + 6}px;
    left: ${button.offsetLeft}px;
    background: white;
    border: 1px solid #6bd888;
    border-radius: 6px;
    padding: 12px 16px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.2);
    z-index: 10000;
    font-family: sans-serif;
    font-size: 13px;
    color: #333;
    min-width: 260px;
    max-width: 360px;
  `;

  popup.innerHTML = `
    <span class="popup-close" style="
      position: absolute;
      top: 4px;
      right: 8px;
      cursor: pointer;
      font-weight: bold;
      font-size: 16px;
      color: #888;
    ">&times;</span>
    <strong style="color:#6bd888; font-size: 14px;">Functional Deficits</strong>
    <p style="margin-top: 6px; margin-bottom: 0; font-size: 13px; line-height: 1.4;">
      This section is automatically linked to the Goals. No action is needed here.
    </p>
  `;

  // attach popup near the button
  button.parentNode.style.position = 'relative'; // make sure parent allows absolute positioning
  button.parentNode.appendChild(popup);

  // close button inside popup
  popup
    .querySelector('.popup-close')
    .addEventListener('click', () => popup.remove());

  // auto-close after 10s
  setTimeout(() => {
    if (popup.parentNode) popup.remove();
  }, 10000);
}

function highlightFunctionalDeficitsSection() {
  const sectionHeader = document.querySelector(
    '#sub_med_hist_func_deficits .Section_label__aimGw'
  );
  if (sectionHeader && !sectionHeader.querySelector('.comprehend-dart-btn')) {
    const bracketButton = document.createElement('span');
    bracketButton.classList.add('comprehend-dart-btn');
    bracketButton.innerHTML = 'Comprehend Alert!';
    bracketButton.style.cssText = `
      display: inline-block;
      margin-left: 8px;
      cursor: pointer;
      font-size: 14px;
      padding: 2px 6px;
      border-radius: 4px;
      background: #6bd888;
      color: white;
      transition: all 0.2s ease;
    `;

    // hover to show popup
    bracketButton.addEventListener('mouseenter', () => {
      bracketButton.style.transform = 'scale(1.1)';
      showFunctionalDeficitsPopup(bracketButton);
    });

    // delay hiding to avoid flicker
    bracketButton.addEventListener('mouseleave', () => {
      bracketButton.style.transform = 'scale(1)';
      setTimeout(() => {
        const popup = document.querySelector('.comprehend-popup');
        if (popup) popup.remove();
      }, 300); // small delay so user can move mouse smoothly
    });

    sectionHeader.appendChild(bracketButton);
  }
}

function handleFunctionalDeficitsSection(
  questionInteractions,
  captureScrape
) {
  const rows = document.querySelectorAll(
    '[class*="FunctionalDeficits_textfield_row"]'
  );

  // Highlight Functional Deficits section and add explanatory text
  if (!captureScrape) {
    highlightFunctionalDeficitsSection();
  }
  return;

  const check = 'Subjective Functional Deficits';
  const nestedKey = 'Functional Deficits';

  questionInteractions[check] = {
    Type: 'Flex',
    Key: check,
    Question: check,
    Structure: {
      [nestedKey]: {}
    },
    ReplyType: 'overwrite',
    OriginalElement: rows[0] || document.body
  };

  let activityCount = 1;
  let customCount = 1;

  rows.forEach((row) => {
    const inputField = row.querySelector('input[type="text"]');
    const dropdownField = row.querySelector('select[name="GOMGNO"]');
    const selects = row.querySelectorAll('select');

    const isCustom = !!inputField;
    const baseName = isCustom
      ? 'Functional Deficit Custom'
      : 'Functional Deficit Activity';
    const index = isCustom ? customCount++ : activityCount++;

    // ACTIVITY field
    const activityKey = `${baseName} ${index}`;
    if (isCustom && inputField) {
      questionInteractions[check].Structure[nestedKey][activityKey] = {
        ...(inputField.value && { 'Existing Value': inputField.value }),
        Input: (val) => {
          if (
            inputField.disabled ||
            isEmptyOrNullorSameAsExisting(val, inputField.value)
          )
            return;
          inputField.value = val;
          inputField.dispatchEvent(new Event('input', { bubbles: true }));
          inputField.dispatchEvent(new Event('change', { bubbles: true }));
        }
      };
    } else if (!isCustom && dropdownField) {
      const options = Array.from(dropdownField.options)
        .map((opt) => opt.textContent.trim())
        .filter((opt) => opt && opt !== '-1' && opt !== '');

      questionInteractions[check].Structure[nestedKey][activityKey] = {
        Choices: options,
        Input: (val) => {
          if (
            dropdownField.disabled ||
            isEmptyOrNullorSameAsExisting(val, dropdownField.value)
          )
            return;
          const match = Array.from(dropdownField.options).find(
            (opt) => opt.textContent.trim() === val
          );
          if (match) {
            dropdownField.value = match.value;
            dropdownField.dispatchEvent(new Event('input', { bubbles: true }));
            dropdownField.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      };
    }

    // PLOF, CLOF, GOAL
    const labels = ['PLOF', 'CLOF', 'Goal'];
    selects.forEach((select, idx) => {
      if (!isCustom && idx === 0) return; // skip Activity select for activity rows

      const label = labels[isCustom ? idx : idx - 1];
      const labelKey = `${baseName} ${label} ${index}`;

      let options = getDropdownOptions(select, label);
      options = options.filter((opt) => opt !== 'N/A' && opt !== '');
      options.push('None');
      questionInteractions[check].Structure[nestedKey][labelKey] = {
        Choices: options,
        Input: (val) => {
          if (
            select.disabled ||
            isEmptyOrNullorSameAsExisting(val, select.value)
          )
            return;
          const match = Array.from(select.options).find(
            (opt) => opt.textContent.trim() === val
          );
          if (match) {
            select.value = match.value;
            select.dispatchEvent(new Event('input', { bubbles: true }));
            select.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      };
    });
  });
}

function handleGoalsSection(questionInteractions) {
  const root = document.querySelector('#goals') || document.body;

  // Existing rows
  const shortRows = document.querySelectorAll(
    '[class*="Common_stg_detail_wrapper"] [class*="Common_stg_detail_row"]'
  );
  const longRows = document.querySelectorAll(
    '[class*="Common_goals_wrapper"] [class*="Common_goal_detail_row"]'
  );
  const customLongRows = document.querySelectorAll(
    '[class*="Common_goal_header"] [class*="Common_goal_detail_row"]'
  );

  // New: Free-Form Goal wrappers
  const freeFormWrappers = document.querySelectorAll(
    `[class*="Common_goal_detail_wrapper__"]`
  );

  // Peds Goal sections
  const pedsGoalSections = document.querySelectorAll(
    '[class*="Common_peds_goal_section__"]'
  );
  console.log('Peds Goal sections:', pedsGoalSections);

  questionInteractions['Goals'] = {
    Type: 'Flex',
    Key: 'Goals',
    Question: 'Goals',
    Structure: {
      'Short Term Goals': createFlatGoalGroup(
        shortRows,
        'Short Term Goal',
        true
      ),
      'Functional Goals': createFlatGoalGroup(longRows, 'Functional Goal'),
      'Custom Functional Goals': createFlatGoalGroup(
        customLongRows,
        'Custom Goal',
        true
      ),
      'Free-Form Goals': createFreeFormGoals(freeFormWrappers),
      'Peds Goals': createPedsGoals(pedsGoalSections)
    },
    OriginalElement: root
  };
}

const createFlatGoalGroup = (rows, initialLabelPrefix, isShortTerm = false) => {
  const flat = {};

  for (let i = 0; i < rows.length; i += isShortTerm ? 1 : 2) {
    const goalBlock = rows[i];
    const performanceBlock = isShortTerm ? rows[i] : rows[i + 1];
    const goalNum = i / (isShortTerm ? 1 : 2) + 1;
    let labelPrefix = initialLabelPrefix;
    const isSTG = initialLabelPrefix === 'Short Term Goal';
    const activityField =
      goalBlock.querySelector(
        'select[name="GOMGNO"], select[class*="Common_ltg_activity_dropdown_req"]'
      ) ||
      goalBlock.querySelector(
        'input[type="text"][class*="Common_ltg_activity_dropdown"]'
      ) ||
      goalBlock.querySelector(
        'div[class*="Common_stg_desc_custom"] input[type="text"]'
      ) ||
      goalBlock.querySelector('select') ||
      goalBlock.querySelector('input[type="text"]');

    const plofField = goalBlock.querySelector('select[name="GOPLOF"]');
    const clofField = goalBlock.querySelector('select[name="GOCLOF"]');
    const goalLevelField = goalBlock.querySelector(
      'select[name="GOGOALLEVEL"]'
    );
    const dateField = goalBlock.querySelector(
      'input[type="text"][class*="DBDateField_picker"], input[type="text"][class*="DBDateField_picker_required"]'
    );
    const performanceField = performanceBlock?.querySelector(
      'input[type="text"][placeholder="Describe how the patient is performing the activity today."]'
    );
    const describeGoalField = goalBlock?.querySelector(
      'input[type="text"][placeholder="Describe or quantify goal"]'
    );
    const shortTermGoalProgressField = goalBlock.querySelector(
      'select[name="SGGPNO"]'
    );
    const longTermGoalProgressField = goalBlock.querySelector(
      'select[name="GOGPNO"]'
    );

    const makeDropdownInput = (field, label) => {
      let options = getDropdownOptions(field, label);
      options = options.filter((opt) => opt !== 'N/A' && opt !== '');
      options.push('None');
      return {
        Choices: options,
        ...(field.value && { 'Existing Value': field.value }),
        Input: (val) => {
          if (
            field.disabled ||
            field.value?.trim() !== '-1' ||
            isEmptyOrNullorSameAsExisting(val, field.value)
          )
            return;
          const match = Array.from(field.options).find(
            (opt) => opt.textContent.trim() === val
          );
          if (match) {
            field.value = match.value;
            field.dispatchEvent(new Event('input', { bubbles: true }));
            field.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      };
    };

    const addInput = (key, value) => {
      if (value) flat[key] = value;
    };

    if (activityField) {
      const activityValue = activityField.value;
      if (!shouldSkipPreselectedField(activityField, activityValue)) {
        labelPrefix =
          labelPrefix +
          ' Activity' +
          (activityValue && activityValue !== '-1' && activityValue !== ''
            ? ' (' + activityValue + ')'
            : '');
      }
      const key = `${labelPrefix} ${goalNum}`;
      if (
        activityField.tagName === 'SELECT' &&
        (activityValue === '-1' || activityValue === '')
      ) {
        addInput(key, makeDropdownInput(activityField, 'Activity'));
      } else if (
        activityField.tagName === 'INPUT' &&
        (activityValue === '-1' || activityValue === '')
      ) {
        addInput(key, {
          ...(activityField.value && { 'Existing Value': activityField.value }),
          ...(isSTG && { Description: 'Enter the full goal.' }),
          Input: (val) => {
            if (
              activityField.disabled ||
              activityField.value?.trim() ||
              isEmptyOrNullorSameAsExisting(val, activityField.value)
            )
              return;
            activityField.value = val;
            activityField.dispatchEvent(new Event('input', { bubbles: true }));
            activityField.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      }
    }

    if (plofField)
      addInput(
        `${labelPrefix} PLOF ${goalNum}`,
        makeDropdownInput(plofField, 'PLOF')
      );
    if (clofField)
      addInput(
        `${labelPrefix} CLOF ${goalNum}`,
        makeDropdownInput(clofField, 'CLOF')
      );
    if (goalLevelField)
      addInput(
        `${labelPrefix} Goal ${goalNum}`,
        makeDropdownInput(goalLevelField, 'Goal')
      );
    if (shortTermGoalProgressField)
      addInput(
        `${labelPrefix} Short Term Goal Progress ${goalNum}`,
        makeDropdownInput(shortTermGoalProgressField, 'Goal Progress')
      );
    if (longTermGoalProgressField)
      addInput(
        `${labelPrefix} Long Term Goal Progress ${goalNum}`,
        makeDropdownInput(longTermGoalProgressField, 'Goal Progress')
      );

    if (dateField) {
      addInput(`${labelPrefix} Date ${goalNum}`, {
        Description: 'Enter the date in the format MM/DD/YYYY',
        Input: (val) => {
          if (
            dateField.disabled ||
            dateField.value?.trim() ||
            isEmptyOrNullorSameAsExisting(val, dateField.value)
          )
            return;
          dateField.value = val;
          dateField.dispatchEvent(new Event('input', { bubbles: true }));
          dateField.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    }

    if (performanceField) {
      addInput(`${labelPrefix} PerformanceDescription ${goalNum}`, {
        ...(performanceField.value && {
          'Existing Value': performanceField.value
        }),
        ...(activityField &&
          activityField.value !== '-1' &&
          activityField.value !== '' && {
          Description: `Enter a goal description about ${activityField.value}`
        }),
        Input: (val) => {
          if (
            performanceField.disabled ||
            performanceField.value?.trim() ||
            isEmptyOrNullorSameAsExisting(val, performanceField.value)
          )
            return;
          performanceField.value = val;
          performanceField.dispatchEvent(new Event('input', { bubbles: true }));
          performanceField.dispatchEvent(
            new Event('change', { bubbles: true })
          );
        }
      });
    }

    // STG Goal Description isn't shown in the EMR; goal goes in the activity field
    if (describeGoalField && !(isSTG && activityField?.tagName === 'INPUT')) {
      addInput(`${labelPrefix} Goal Description ${goalNum}`, {
        ...(describeGoalField.value && {
          'Existing Value': describeGoalField.value
        }),
        Description: `Enter a goal description about ${activityField.value}`,
        Input: (val) => {
          if (
            describeGoalField.disabled ||
            describeGoalField.value?.trim() ||
            isEmptyOrNullorSameAsExisting(val, describeGoalField.value)
          )
            return;
          describeGoalField.value = val;
          describeGoalField.dispatchEvent(
            new Event('input', { bubbles: true })
          );
          describeGoalField.dispatchEvent(
            new Event('change', { bubbles: true })
          );
        }
      });
    }
  }

  return flat;
};

const createFreeFormGoals = (wrappers) => {
  const flat = {};

  wrappers.forEach((wrapper, index) => {
    const goalNum = index + 1;

    const nameField = wrapper.querySelector(
      'input[type="text"][class*="Common_text_field"]'
    );
    const progressField = wrapper.querySelector('select[name="GOGPNO"]');
    const goalTextField = wrapper.querySelector(
      'textarea[class*="Common_text_area"]'
    );
    const dateField = wrapper.querySelector(
      'input[type="text"][class*="DBDateField_picker"]'
    );

    const addInput = (key, value) => {
      if (value) flat[key] = value;
    };
    let nameValue = '';
    if (nameField) {
      nameValue = nameField.value;
      if (!shouldSkipPreselectedField('name', nameValue)) {
        addInput(
          nameValue
            ? `Free-Form Goal Name (${nameValue}) ${goalNum}`
            : `Free-Form Goal Name ${goalNum}`,
          {
            ...(nameField.value && { 'Existing Value': nameField.value }),
            Input: (val) => {
              if (
                nameField.disabled ||
                nameField.value?.trim() ||
                isEmptyOrNullorSameAsExisting(val, nameField.value)
              )
                return;
              nameField.value = val;
              nameField.dispatchEvent(new Event('input', { bubbles: true }));
              nameField.dispatchEvent(new Event('change', { bubbles: true }));
            }
          }
        );
      }
    }

    if (progressField) {
      addInput(
        nameValue
          ? `Free-Form Goal Progress (${nameValue}) ${goalNum}`
          : `Free-Form Goal Progress ${goalNum}`,
        {
          Choices: Array.from(progressField.options)
            .map((opt) => opt.textContent.trim())
            .filter((opt) => opt && opt !== '-1' && opt !== 'None'),
          ...(progressField.value && { 'Existing Value': progressField.value }),
          Input: (val) => {
            if (
              progressField.disabled ||
              progressField.value?.trim() !== '-1' ||
              isEmptyOrNullorSameAsExisting(val, progressField.value)
            )
              return;
            const match = Array.from(progressField.options).find(
              (opt) => opt.textContent.trim() === val
            );
            if (match) {
              progressField.value = match.value;
              progressField.dispatchEvent(
                new Event('input', { bubbles: true })
              );
              progressField.dispatchEvent(
                new Event('change', { bubbles: true })
              );
            }
          }
        }
      );
    }

    if (goalTextField) {
      addInput(
        nameValue
          ? `Free-Form Goal Goal (${nameValue}) ${goalNum}`
          : `Free-Form Goal Goal ${goalNum}`,
        {
          ...(goalTextField.value && { 'Existing Value': goalTextField.value }),
          Description: `Enter a goal description about ${nameField.value}${'maxLength' in goalTextField && goalTextField.maxLength > 0 ? `. Your response must not exceed ${goalTextField.maxLength} characters.` : ''}`,
          Input: (val) => {
            if (
              goalTextField.disabled ||
              goalTextField.value?.trim() ||
              isEmptyOrNullorSameAsExisting(val, goalTextField.value)
            )
              return;
            goalTextField.value = val;
            goalTextField.dispatchEvent(new Event('input', { bubbles: true }));
            goalTextField.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      );
    }

    if (dateField) {
      addInput(
        nameValue
          ? `Free-Form Goal Date (${nameValue}) ${goalNum}`
          : `Free-Form Goal Date ${goalNum}`,
        {
          Description: 'Enter the date in the format MM/DD/YYYY',
          Input: (val) => {
            if (
              dateField.disabled ||
              dateField.value?.trim() ||
              isEmptyOrNullorSameAsExisting(val, dateField.value)
            )
              return;
            dateField.value = val;
            dateField.dispatchEvent(new Event('input', { bubbles: true }));
            dateField.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      );
    }
  });

  return flat;
};

const createPedsGoals = (sections) => {
  const flat = {};

  sections.forEach((section, sectionIndex) => {
    const goalNum = sectionIndex + 1;

    // Find the activity dropdown (GOMGNO) - it's in the first row
    const activityField = section.querySelector(
      'select[name="GOMGNO"]'
    ) as HTMLSelectElement | null;

    // Find all rows within the section - there should be two rows
    const rows = Array.from(
      section.querySelectorAll('[class*="Common_peds_current_row__"]')
    );
    const currentRow = rows[0] as HTMLElement | undefined;
    const goalRow = rows[1] as HTMLElement | undefined;

    // Current row fields
    const currentLevelField = currentRow?.querySelector(
      'select[name="GOLONOCURRENT"]'
    ) as HTMLSelectElement | null;
    const currentDescriptionField = currentRow?.querySelector(
      'input[class*="Common_peds_txt"]'
    ) as HTMLInputElement | null;

    // Goal row fields
    const goalLevelField = goalRow?.querySelector(
      'select[name="GOLONOGOAL"]'
    ) as HTMLSelectElement | null;
    const goalDescriptionField = goalRow?.querySelector(
      'input[class*="Common_peds_txt"]'
    ) as HTMLInputElement | null;
    const dateField = goalRow?.querySelector(
      'input[class*="DBDateField_picker"]'
    ) as HTMLInputElement | null;

    const addInput = (key, value) => {
      if (value) flat[key] = value;
    };

    const makeDropdownInput = (
      field: HTMLSelectElement | null,
      label: string
    ) => {
      if (!field) return null;
      let options = Array.from(field.options)
        .map((opt) => opt.textContent?.trim() || '')
        .filter((opt) => opt && opt !== '-1' && opt !== '');
      options.push('None');
      return {
        Choices: options,
        ...(field.value &&
          field.value !== '-1' && { 'Existing Value': field.value }),
        Input: (val: string) => {
          if (
            field.disabled ||
            (field.value && field.value !== '-1') ||
            isEmptyOrNullorSameAsExisting(val, field.value)
          )
            return;
          const match = Array.from(field.options).find(
            (opt) => opt.textContent?.trim() === val
          );
          if (match) {
            field.value = match.value;
            field.dispatchEvent(new Event('input', { bubbles: true }));
            field.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      };
    };

    let labelPrefix = 'Peds Goal';

    // Activity field
    if (activityField) {
      const activityValue = activityField.value;
      const selectedOption = Array.from(activityField.options).find(
        (opt) => opt.value === activityValue
      );
      const activityText = selectedOption?.textContent?.trim();

      if (!shouldSkipPreselectedField(activityField, activityValue)) {
        labelPrefix = activityText
          ? `${labelPrefix} Activity (${activityText})`
          : `${labelPrefix} Activity`;
      } else if (activityText) {
        labelPrefix = `${labelPrefix} (${activityText})`;
      }

      const activityKey = `${labelPrefix} ${goalNum}`;
      if (activityValue === '-1' || activityValue === '') {
        addInput(activityKey, makeDropdownInput(activityField, 'Activity'));
      }
    }

    // Current Level of Assistance
    if (currentLevelField) {
      addInput(
        `${labelPrefix} Current Level of Assistance ${goalNum}`,
        makeDropdownInput(currentLevelField, 'Current Level of Assistance')
      );
    }

    // Current Description
    if (currentDescriptionField) {
      addInput(`${labelPrefix} Current Description ${goalNum}`, {
        ...(currentDescriptionField.value && {
          'Existing Value': currentDescriptionField.value
        }),
        Input: (val) => {
          if (
            currentDescriptionField.disabled ||
            currentDescriptionField.value?.trim() ||
            isEmptyOrNullorSameAsExisting(val, currentDescriptionField.value)
          )
            return;
          currentDescriptionField.value = val;
          currentDescriptionField.dispatchEvent(
            new Event('input', { bubbles: true })
          );
          currentDescriptionField.dispatchEvent(
            new Event('change', { bubbles: true })
          );
        }
      });
    }

    // Goal Level of Assistance
    if (goalLevelField) {
      addInput(
        `${labelPrefix} Goal Level of Assistance ${goalNum}`,
        makeDropdownInput(goalLevelField, 'Goal Level of Assistance')
      );
    }

    // Goal Description
    if (goalDescriptionField) {
      addInput(`${labelPrefix} Goal Description ${goalNum}`, {
        ...(goalDescriptionField.value && {
          'Existing Value': goalDescriptionField.value
        }),
        Input: (val) => {
          if (
            goalDescriptionField.disabled ||
            goalDescriptionField.value?.trim() ||
            isEmptyOrNullorSameAsExisting(val, goalDescriptionField.value)
          )
            return;
          goalDescriptionField.value = val;
          goalDescriptionField.dispatchEvent(
            new Event('input', { bubbles: true })
          );
          goalDescriptionField.dispatchEvent(
            new Event('change', { bubbles: true })
          );
        }
      });
    }

    // Date field
    if (dateField) {
      addInput(`${labelPrefix} Date ${goalNum}`, {
        Description: 'Enter the date in the format MM/DD/YYYY',
        Input: (val) => {
          if (
            dateField.disabled ||
            dateField.value?.trim() ||
            isEmptyOrNullorSameAsExisting(val, dateField.value)
          )
            return;
          dateField.value = val;
          dateField.dispatchEvent(new Event('input', { bubbles: true }));
          dateField.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    }
  });

  return flat;
};

function getDropdownOptions(select, label) {
  let options = Array.from(select.options)
    .map((opt) => opt.textContent.trim())
    .filter((opt) => opt && opt !== '-1' && opt !== '');

  if (options.length === 0 && ['PLOF', 'Goal', 'CLOF'].includes(label)) {
    options = [
      '2 - with maximal difficulty',
      '3 - with moderate difficulty',
      '4 - with minimal difficulty',
      '5 - without difficulty'
    ];

    if (label === 'CLOF') {
      options.unshift('1 - unable to perform');
    }
  }

  return options;
}

// Helper function to find the closest `.Common_label`
function findClosestLabel(element) {
  let current = element;

  while (current && !current.matches('body')) {
    let labelElement = current.querySelector('.Common_label');
    if (labelElement && labelElement.textContent.trim()) {
      return labelElement.textContent.trim();
    }

    //Check one level higher's label too
    const oneLevelUp = current.parentElement;
    if (oneLevelUp) {
      let upperLabel = oneLevelUp.querySelector('.Common_label');
      if (upperLabel && upperLabel.textContent.trim()) {
        return upperLabel.textContent.trim();
      }
    }

    // Move up
    current = current.parentElement;
  }
  return null;
}

// Helper function to extract the hierarchy for a checkbox
function extractHierarchy(checkbox) {
  let hierarchy = [];
  let currentElement = checkbox;

  // Traverse up the DOM hierarchy
  while (currentElement && !currentElement.matches('body')) {
    if (currentElement.matches('span')) {
      const sectionLabel = currentElement.querySelector(
        '[class*="Section_label"]'
      );
      if (sectionLabel) {
        const labelText = sectionLabel.textContent.trim();
        if (!hierarchy.includes(labelText)) {
          hierarchy.unshift(labelText); // Add to the hierarchy in reverse order
        }
      }
    }
    currentElement = currentElement.parentElement;
  }
  hierarchy = cleanSubjectiveCheckBoxHierarchy(hierarchy);

  return hierarchy.length > 0 ? hierarchy.join(' ') : null;
}

function cleanSubjectiveCheckBoxHierarchy(arr) {
  // If 'Comorbidity' is mentioned explicitly, leave everything as-is
  if (arr.includes('Comorbidity')) return arr;

  // If 'Comorbidity' is not present, remove 'History' only if there's something else like 'Sleep'
  const hasExtraFields = arr.some(
    (item) => item !== 'Subjective' && item !== 'History'
  );
  if (hasExtraFields) {
    return arr.filter((item) => item !== 'History');
  }

  return arr;
}

function determineInputType(labelElement) {
  let type = 'String'; // Default to string

  if (labelElement) {
    const title = labelElement.getAttribute('title');
    const min = labelElement.getAttribute('min');
    const max = labelElement.getAttribute('max');

    const hasNumericTitle =
      title &&
      (/(\d+\s*(-|to)\s*\d+)/i.test(title) || // e.g., "5 - 10"
        /greater than or equal to/i.test(title) || // "greater than or equal to"
        /^Normative Value:\s*\d+(\.\d+)?$/i.test(title.trim())); // "Normative Value: 0" or decimal

    const hasMinMax = min !== null || max !== null;

    if (hasNumericTitle || hasMinMax) {
      type = 'Integer';
    }
  }

  return type;
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// Helper function to prevent overwriting values unnecessarily.
// Specifically important for dropdowns in Cora Health, where setting the same value again can clear adjacent fields.
function isEmptyOrNullorSameAsExisting(aiResponse, existingValue) {
  return (
    aiResponse === null ||
    aiResponse === undefined ||
    aiResponse === '' ||
    aiResponse === 'None' ||
    aiResponse === existingValue
  );
}

// Skip Activity or To Improve fields if the user has already selected a valid option.
// This prevents AI from overwriting manually selected values with new responses.
// Comprehend should only suggest values for fields that are currently empty or set to default.
function shouldSkipPreselectedField(field, selectedLabel) {
  const isSkippable =
    field === 'Activity' || field === 'To Improve' || field === 'name';
  const isPreselected = selectedLabel !== '-1' && selectedLabel !== '';
  return isSkippable && isPreselected;
}

export async function answerQuestions(answers) {
  var errors = [];

  // Use Promise.all to ensure all questions are processed before moving on
  const answerPromises = answers.map(async (answer) => {
    var error = await answerQuestion(answer['Question'], answer['Answer']);
    if (error) {
      errors.push({
        Question: answer['Question'],
        Answer: answer['Answer'],
        Error: error
      });
    }
  });

  await Promise.all(answerPromises); // Wait for all answers to process

  console.error('Errors:', errors);
  return errors;
}

var isLocked = false;
export async function answerQuestion(
  question,
  answer,
  replyType,
  totalWaitTime = 0
) {
  if (!questionInteractions[question]) {
    console.error(`Question not found: "${question}"`);
    return 'Question not found';
  }
  // function answerQuestion(question, answer) {
  /*
      Description: This function will take the answer and populate the question
      
      Args: 
          question: The question in question
          answer: The answer to the question
     
      Return: null if success otherwise a string with the error message
      */

  // console.log("Answering Question: '" + question + "' with '" + answer+"'")

  // If it's a flex do so
  if (questionInteractions[question]['Type'] === 'Flex') {
    return await answerFlexQuestion(question, answer, replyType);
  }
  // Make sure the question exists
  if (questionInteractions[question] == null) {
    console.error('Question not found');
    console.log(question);
    // console.log(questionInteractions)
    // console.log(Object.keys(questionInteractions))

    return 'Question not found';
  }

  // if we are locked, delay for a random amount of time between .1 and 1 seconds waiting no longer than 3 seconds
  if (isLocked && totalWaitTime < 3000) {
    let delay = Math.random() * 1000;
    return sleep(delay).then(() => {
      return answerQuestion(question, answer, replyType, totalWaitTime + delay);
    });
  }

  // Se our lock
  isLocked = true;

  try {
    // If it is a short answer or long answer, just populate the input
    if (questionInteractions[question]['Input']) {
      await questionInteractions[question]['Input'](answer, replyType);
      isLocked = false;
      return true;
    }

    // Check if the answer is a list of answers or just a single answer
    if (Array.isArray(answer)) {
      // Choose All
      var notFoundAnswers = [];
      for (var i = 0; i < answer.length; i++) {
        if (answer[i] in questionInteractions[question]['Choices']) {
          console.log('Answer found ' + answer[i]);
          await questionInteractions[question]['Choices'][answer[i]](true);
          // sleep(1000).then(() => {
          //     questionInteractions[question][answer[i]]()
          // });
        } else {
          notFoundAnswers.push(answer[i]);
        }
      }
      if (notFoundAnswers.length > 0) {
        console.error(
          'The following answers were not found: ' + notFoundAnswers
        );
        return 'The following answers were not found: ' + notFoundAnswers;
      } else {
        return true;
      }
    } else {
      // Multiple Choice
      if (answer in questionInteractions[question]['Choices']) {
        await questionInteractions[question]['Choices'][answer]();
        return true;
      } else {
        console.error('Answer not found');
        // console.log('Question: ' + question);
        // console.log('Answer:' + answer);
        // console.log(questionInteractions[question]['Choices']);
        return 'Answer not found';
      }
    }
  } finally {
    isLocked = false;
  }
}

async function answerFlexQuestion(
  question,
  answer,
  replyType = 'overwrite',
  totalWaitTime = 0
) {
  try {
    // If we are locked, delay for a random amount of time between 0.1 and 1 seconds, waiting no longer than 3 seconds
    if (isLocked && totalWaitTime < 3000) {
      let delay = Math.random() * 1000;
      return sleep(delay).then(() => {
        return answerFlexQuestion(
          question,
          answer,
          replyType,
          totalWaitTime + delay
        );
      });
    }

    // Set the lock
    isLocked = true;

    const errors = [];
    const questionObject = cleanSubsectionNames(questionInteractions[question]);

    if (!questionObject) {
      console.error(`Question with Key "${question}" not found.`);
      return {
        Question: question,
        Error: `Question with Key "${question}" not found.`
      };
    }

    const structure = questionObject.Structure || {};

    /** Do the answering sequentially */
    for (const [fieldKey, fieldDetails] of Object.entries(answer)) {
      const field = structure[fieldKey];

      if (!field) {
        console.error(`Field "${fieldKey}" not found in the Structure.`);
        errors.push({ Field: fieldKey, Error: 'Field not found in Structure' });
        continue;
      }

      for (const [subFieldKey, value] of Object.entries(fieldDetails)) {
        // Handle $-separated values for checkbox inputs
        const valuesToInput =
          typeof value === 'string'
            ? value.split('$').map((v) => v.trim())
            : [value];

        for (const val of valuesToInput) {
          if (field[subFieldKey]?.Input) {
            try {
              if (/(Functional Goal).*(PLOF|CLOF|Goal)/.test(subFieldKey)) {
                await sleep(200);
              }
              await field[subFieldKey].Input(val, replyType);
            } catch (error) {
              errors.push({
                Field: `${fieldKey}.${subFieldKey}`,
                Error: `Error applying value: ${error.message}`
              });
            }
          } else {
            console.warn(
              `Input function not found for "${fieldKey}.${subFieldKey}"`
            );
            errors.push({
              Field: `${fieldKey}.${subFieldKey}`,
              Error: 'Input function not found'
            });
          }
        }
      }
    }

    /** Parse the errors */
    if (errors.length > 0) {
      console.error('Errors occurred:', errors);
      return errors;
    }

    return null; // Success
  } catch (error) {
    console.error('Unexpected error:', error);
    return {
      Question: question,
      Error: `Unexpected error: ${error.message}`
    };
  } finally {
    // Release the lock
    isLocked = false;
  }
}

export async function getLogFromInjector() {
  try {
    const htmlContent = document.documentElement.outerHTML;
    chrome.runtime.sendMessage({
      type: 'INJECTOR_ERROR',
      error: { htmlContent: htmlContent }
    });
    return { htmlContent: htmlContent };
  } catch (error) {
    // Return the full HTML content of the document
    console.error('Error occurred while fetching document:', error);
    getLogFromInjector();
  }
}
